<template>
    <fieldset :disabled="isDisable">
        <div class="row">
            <div class="col-lg-12">
                <form ref="form" class="mb-0 position-relative" :class="{ 'loading-opacity': loading, 'walkin-mode': isWalkin }"
                    :data-url='selectedUrl ? selectedUrl : CUSTOMERS' @submit.prevent="submitData">
                    <app-overlay-loader v-if="loading" />

                    <!-- ===== Section 1: Patient Type ===== -->
                    <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Patient Type') }}</h5>
                    <div class="row">
                        <div class="col-12">
                            <app-form-group type="radio" radio-checkbox-name="patient_type" :list="patientTypeOptions"
                                v-model="formData.customer_group_id"
                                :error-message="$errorMessage(errors, 'customer_group_id')" />
                        </div>
                    </div>

                    <!-- ===== Section 2: Personal Information ===== -->
                    <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Personal Information') }}</h5>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-2">
                            <app-form-group type="select" :list="Prefix" :class="'required always-required'" list-value-field="name"
                                :label="$t('Prefix')" :required="true" :error-message="$errorMessage(errors, 'prefix')"
                                :placeholder="$placeholder('Prefix')" v-model="formData.prefix" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-3">
                            <app-form-group :label="$t('first_name')" :class="'required always-required'" v-model="formData.first_name"
                                :error-message="$errorMessage(errors, 'first_name')" :placeholder="$placeholder($t('first_name'))" :required="true" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-3">
                            <app-form-group :label="$t('last_name')" :class="'required'" v-model="formData.last_name"
                                :error-message="$errorMessage(errors, 'last_name')" :placeholder="$placeholder($t('last_name'))" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-4">
                            <app-form-group :label="$t('Patient Name Hindi')" v-model="formData.patient_name_hindi"
                                :error-message="$errorMessage(errors, 'patient_name_hindi')"
                                :placeholder="$placeholder($t('Patient Name Hindi'))" />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-4">
                            <label>{{ $t('Primary Email') }}</label>
                            <div class="row no-gutters">
                                <div class="col-10">
                                    <app-input v-model="inputEmailRow[0].email" id="primary_email"
                                        :error-message="$errorMessage(errors, 'email_details.0.email')"
                                        :placeholder="$placeholder($t('email'))" />
                                </div>
                                <div class="col-2 pl-1">
                                    <button class="btn default-base-color btn-block" type="button" @click="toggleSecondaryEmail">
                                        <app-icon class="size-19 primary-text-color" :name="showSecondaryEmail ? 'trash' : 'plus'" />
                                    </button>
                                </div>
                            </div>
                            <div v-if="showSecondaryEmail" class="mt-2">
                                <label>{{ $t('Secondary Email') }}</label>
                                <app-input v-model="inputEmailRow[1].email" id="secondary_email"
                                    :error-message="$errorMessage(errors, 'email_details.1.email')"
                                    :placeholder="$placeholder($t('email'))" />
                            </div>
                        </div>
                        <div class="col-12 col-sm-6 col-md-4 required">
                            <label>{{ $t('Primary Phone') }}</label>
                            <div class="row no-gutters">
                                <div class="col-4 pr-1">
                                    <select v-model="inputPhoneNumberRow[0].country_code" class="form-control px-1">
                                        <option v-for="cc in phoneCountryCodes" :key="'p0' + cc.code" :value="cc.code">{{ cc.code }} {{ cc.name }}</option>
                                    </select>
                                </div>
                                <div class="col-6">
                                    <app-input v-model="inputPhoneNumberRow[0].phone_number" type="number" id="primary_phone"
                                        :error-message="$errorMessage(errors, 'phone_number_details.0.phone_number')"
                                        :placeholder="$placeholder($t('phone_number'))" />
                                </div>
                                <div class="col-2 pl-1">
                                    <button class="btn default-base-color btn-block" type="button" @click="toggleSecondaryPhone">
                                        <app-icon class="size-19 primary-text-color" :name="showSecondaryPhone ? 'trash' : 'plus'" />
                                    </button>
                                </div>
                            </div>
                            <div v-if="showSecondaryPhone" class="mt-2">
                                <label>{{ $t('Secondary Phone') }}</label>
                                <div class="row no-gutters">
                                    <div class="col-4 pr-1">
                                        <select v-model="inputPhoneNumberRow[1].country_code" class="form-control px-1">
                                            <option v-for="cc in phoneCountryCodes" :key="'p1' + cc.code" :value="cc.code">{{ cc.code }} {{ cc.name }}</option>
                                        </select>
                                    </div>
                                    <div class="col-8">
                                        <app-input v-model="inputPhoneNumberRow[1].phone_number" type="number" id="secondary_phone"
                                            :error-message="$errorMessage(errors, 'phone_number_details.1.phone_number')"
                                            :placeholder="$placeholder($t('phone_number'))" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6 col-md-4">
                            <app-form-group type="date" v-model="formData.registration_date" :readOnly="true"
                                :error-message="$errorMessage(errors, 'registration_date')" :label="$t('Registration Date')"
                                :placeholder="$placeholder($t('Registration Date'))" />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-3">
                            <app-form-group :class="'required always-required'" type="select" :label="$t('Gender')" :list="[
                                { id: '', name: $t('Select Gender') },
                                { id: 'male', name: $t('male') },
                                { id: 'female', name: $t('female') },
                                { id: 'other', name: $t('other') }
                            ]" list-value-field="name" :page="'modal'" v-model="formData.gender"
                                :error-message="$errorMessage(errors, 'gender')" :placeholder="$placeholder($t('Gender'))" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-3 required">
                            <app-form-group type="date" :label="$t('Date of birth')" :class="'required'"
                                v-model="formData.date_of_birth" :error-message="$errorMessage(errors, 'date_of_birth')"
                                :placeholder="'dd-mm-yyyy'" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-3 required">
                            <label>{{ $t('Age') }}</label>
                            <app-input type="number" v-model="formData.age" :readOnly="true"
                                :error-message="$errorMessage(errors, 'age')" :placeholder="'Auto'" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-3 required">
                            <app-form-group type="radio" radio-checkbox-name="legacy_patient" :label="$t('Legacy Patient')" :list="[
                                { id: 'No', value: $t('No') },
                                { id: 'Yes', value: $t('Yes') }
                            ]" v-model="formData.legacy_patient"
                                :error-message="$errorMessage(errors, 'legacy_patient')" />
                        </div>
                    </div>

                    <!-- ===== Section 3: Branch Information ===== -->
                    <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Branch Information') }}</h5>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-4 required always-required">
                            <label>{{ $t('Original Branch') }}</label>
                            <div class="form-group">
                                <select id="original_branch" class="original_branch form-control "
                                    name="original_branch" v-model="formData.original_branch" style="width: 100%;" :disabled="!canReassign">
                                    <option v-for="branch in branchList" :key="branch.id" :value="branch.id">{{ branch.value }}</option>
                                </select>
                                <small class="text-danger" v-if="$errorMessage(errors, 'original_branch')">{{ $errorMessage(errors, 'original_branch') }}</small>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6 col-md-4 required">
                            <label>{{ $t('Current Branch') }}</label>
                            <div class="form-group">
                                <select id="current_branch" class="form-control"
                                    name="current_branch" v-model="formData.current_branch" style="width: 100%;" :disabled="!canReassign">
                                    <option value="">{{ $t('Select Branch') }}</option>
                                    <option v-for="branch in branchList" :key="'cb' + branch.id" :value="branch.id">{{ branch.value }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6 col-md-4">
                            <app-form-group type="select" :list="ExaminationVenue" list-value-field="name" :page="'modal'"
                                :label="$t('Examination Venue')" :required="true"
                                :error-message="$errorMessage(errors, 'examination_venue')"
                                :placeholder="$placeholder('Examination Venue')" v-model="formData.examination_venue" />
                        </div>
                    </div>

                    <!-- ===== Section 4: Clinical Information ===== -->
                    <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Clinical Information') }}</h5>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-3 required">
                            <label>{{ $t('Primary complaint') }}</label>
                            <div class="form-group">
                                <select id="primary_complaint" class="primary_complaint form-control "
                                    name="primary_complaint" v-model="formData.primary_complaint" style="width: 100%;" :disabled="isDisable">
                                    <option v-for="primaryComplaint in primaryComplaintList" :key="primaryComplaint.id" :value="primaryComplaint.id">{{ primaryComplaint.value }}</option>
                                </select>
                                <small class="text-danger" v-if="$errorMessage(errors, 'primary_complaint')">{{ $errorMessage(errors, 'primary_complaint') }}</small>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6 col-md-3">
                            <app-form-group :class="'required'" type="radio" radio-checkbox-name="diabetic" :label="$t('Diabetic')" :list="[
                                { id: 'No', value: $t('No') },
                                { id: 'Yes', value: $t('Yes') }
                            ]" v-model="formData.diabetic"
                                :error-message="$errorMessage(errors, 'diabetic')" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-3 required">
                            <label>{{ $t('Current Weight') }} ({{ weightUnit || 'kgs' }})</label>
                            <app-input type="number" v-model="formData.weight" :error-message="$errorMessage(errors, 'weight')"
                                :placeholder="$placeholder($t('Current Weight'))" onkeydown="if(event.key==='.'){event.preventDefault();}"
                                oninput="event.target.value = event.target.value.replace(/[^0-9]*/g,'');" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-3">
                            <app-form-group type="select" :list="ActivityLevel" list-value-field="name" :page="'modal'"
                                v-model="formData.activity_level" :error-message="$errorMessage(errors, 'activity_level')"
                                :label="$t('Activity Level')" :placeholder="$placeholder($t('Activity Level'))" />
                        </div>
                    </div>

                    <!-- ===== Section 5: Important Notes ===== -->
                    <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Important Notes') }}</h5>
                    <div class="row">
                        <div class="col-12">
                            <app-form-group type="textarea" :text-area-rows="3" v-model="formData.important_notes"
                                :error-message="$errorMessage(errors, 'important_notes')"
                                :placeholder="$placeholder($t('Important Notes'))" />
                        </div>
                    </div>

                    <!-- ===== Section 6: How Did You Find Us? ===== -->
                    <h5 class="section-title border-bottom mb-2 pb-2">{{ $t('How Did You Find Us?') }}</h5>
                    <p class="text-muted mb-3"><small>* {{ $t('Please fill at least one field below') }}</small></p>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-4 required">
                            <label>{{ $t('Referred by') }}</label>
                            <div class="form-group">
                                <select id="referred_by" class="referredby form-control "
                                    name="referred_by" v-model="formData.referred_by" style="width: 100%;">
                                    <option v-for="row in referredBy" :key="row.id" :value="row.id">{{ row.value }}</option>
                                </select>
                                <small class="text-danger" v-if="$errorMessage(errors, 'referred_by')">{{ $errorMessage(errors, 'referred_by') }}</small>
                            </div>
                        </div>
                        <div :class="getSourceColumnClass() + ' required'">
                            <app-form-group type="select" :class="'required'" :list="Source" input-class=" form_source" list-value-field="name"
                                :page="'modal'" v-model="formData.source" :error-message="$errorMessage(errors, 'source')"
                                :label="$t('How Did You Hear About Us')" :placeholder="$placeholder($t('How Did You Hear About Us'))" />
                        </div>
                        <div :class="getCampColumnClass()">
                            <label>{{ $t('Camp Name') }}</label>
                            <div class="form-group">
                                <select id="camp_name" class="campname form-control "
                                    name="camp_name" v-model="formData.camp_name" style="width: 100%;">
                                    <option v-for="row in campList" :key="row.id" :value="row.id">{{ row.value }}</option>
                                </select>
                                <small class="text-danger" v-if="$errorMessage(errors, 'camp_name')">{{ $errorMessage(errors, 'camp_name') }}</small>
                            </div>
                        </div>
                    </div>

                    <!-- ===== Section 7: Additional Information ===== -->
                    <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Additional Information') }}</h5>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-6">
                            <app-form-group type="select" :class="'required'" :list="PreferredLanguage" list-value-field="name" :page="'modal'"
                                v-model="formData.preferred_language" :error-message="$errorMessage(errors, 'preferred_language')"
                                :label="$t('Preferred language')" :placeholder="$placeholder($t('Preferred language'))" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-6">
                            <app-form-group v-model="formData.occupation" :error-message="$errorMessage(errors, 'occupation')"
                                :label="$t('Occupation')" :placeholder="$placeholder($t('Job Title'))" />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-6">
                            <app-form-group :label="$t('Linked patients')" v-model="formData.linked_patients"
                                :error-message="$errorMessage(errors, 'linked_patients')"
                                :placeholder="$placeholder($t('Patient Names, comma-separated'))" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-6">
                            <app-form-group :label="$t('Guardian Info')" v-model="formData.guardian_name_relationship"
                                maxlength="30" :error-message="$errorMessage(errors, 'guardian_name_relationship')"
                                :placeholder="$placeholder($t('Name & relationship'))" />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-4">
                            <app-form-group v-model="formData.discount" :error-message="$errorMessage(errors, 'discount')"
                                :label="$t('Discount %')" :placeholder="$placeholder($t('Discount'))" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-4">
                            <app-form-group :label="$t('Satisfaction status')" type="radio" radio-checkbox-name="satisfaction_status" :list="[
                                { id: 'N/A', value: $t('N/A') },
                                { id: 'Yes', value: $t('Yes') },
                                { id: 'No', value: $t('No') }
                            ]" v-model="formData.satisfaction_status"
                                :error-message="$errorMessage(errors, 'satisfaction_status')" />
                        </div>
                        <div class="col-12 col-sm-6 col-md-4">
                            <app-form-group :label="$t('Do Not Disturb')" type="radio" radio-checkbox-name="dnd" :list="[
                                { id: 0, value: $t('No') },
                                { id: 1, value: $t('Yes') }
                            ]" v-model="formData.dnd" :error-message="$errorMessage(errors, 'dnd')" />
                        </div>
                    </div>

                    <!-- ===== Section 8: Practitioner Information ===== -->
                    <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Practitioner Information') }}</h5>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-4">
                            <app-form-group v-model="formData.record_created_by"
                                :error-message="$errorMessage(errors, 'record_created_by')" :label="$t('Record Created By')"
                                :placeholder="$placeholder($t('Record Created By'))" disabled />
                        </div>
                        <div class="col-12 col-md-4 mb-4">
                            <label>{{ $t('First Assigned To') }}</label>
                            <div class="form-group">
                                <select id="first_assigned_to" class="first_assigned_to form-control "
                                    name="first_assigned_to" v-model="formData.first_assigned_to"
                                    :disabled="!canReassign" style="width: 100%;">
                                    <option v-for="user in userAssignedList" :key="user.id" :value="user.id">{{ user.value }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-12 col-md-4 mb-4">
                            <label>{{ $t('Currently Assigned To') }}</label>
                            <div class="form-group">
                                <select id="currently_assigned_to" class="currently_assigned_to form-control "
                                    name="currently_assigned_to" v-model="formData.currently_assigned_to"
                                    style="width: 100%;" :disabled="!canReassign">
                                    <option v-for="user in userList" :key="user.id" :value="user.id">{{ user.value }}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div v-if="userCanUpdateCustomer && !isDisable" class="mt-primary mb-4">
                        <button class="btn btn-primary" type="button" @click.prevent="submitData">
                            {{ $t('save') }}
                        </button>
                    </div>
                    <hr>
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-12">
                            <!-- Load app-customer-address component dynamically -->
                            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Address') }}</h5>
                            <app-customer-address :props="this.props"></app-customer-address>
                        </div>
                    </div>
                    <hr>
                    <!-- Attachments Section -->
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-12 mb-4">
                            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Attachments') }}</h5>
                            <div class="d-flex flex-wrap">
                                <div v-for="attachment in inputAttachments" :key="attachment.id"
                                    class="text-center mr-3 mb-3" style="width:150px;">
                                    <img v-if="isImageAttachment(attachment.file_path)"
                                        :src="urlGenerator(attachment.file_path)"
                                        style="height:130px; max-width:100%; object-fit:cover; cursor:zoom-in; border:1px solid #eee; border-radius:4px;"
                                        :title="$t('Click to enlarge')"
                                        @click="openLightbox(urlGenerator(attachment.file_path))" />
                                    <div v-else
                                        style="height:130px; display:flex; align-items:center; justify-content:center; border:1px solid #eee; border-radius:4px;">
                                        <app-icon name="file" class="size-40" />
                                    </div>
                                    <div class="text-truncate small mt-1" :title="getAttachmentFileName(attachment.file_path)">
                                        {{ getAttachmentFileName(attachment.file_path) }}
                                    </div>
                                    <div class="mt-1">
                                        <a v-if="isImageAttachment(attachment.file_path)" class="btn btn-sm btn-link p-0 mr-2"
                                            :title="$t('Enlarge')" @click="openLightbox(urlGenerator(attachment.file_path))"><app-icon name="maximize-2"/></a>
                                        <a v-else class="btn btn-sm btn-link p-0 mr-2"
                                            :title="$t('View')" :href="urlGenerator(attachment.file_path)" target="_blank"><app-icon name="eye"/></a>
                                        <a class="btn btn-sm btn-link p-0 mr-2" :title="$t('Download')"
                                            :href="urlGenerator(attachment.file_path)" :download="getAttachmentFileName(attachment.file_path)"><app-icon name="download"/></a>
                                        <a v-if="!isDisable" class="btn btn-sm btn-link text-danger p-0"
                                            :title="$t('Delete')" @click="() => deleteAttachment(attachment.id)"><app-icon name="trash-2"/></a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6 col-md-12 mb-4">
                            <div v-for="(attachment, index) in attachments" :key="index">
                                <div class="row mb-3">
                                    <div class="col-md-6">
                                        <label>Description <span class="text-danger">*</span></label>
                                        <input class="form-control" type="text" v-model="attachment.description"
                                            placeholder="Description" />
                                    </div>
                                    <div class="col-md-5">
                                        <label>Files</label>
                                        <input class="form-control" type="file" accept=".jpg, .jpeg, .gif, .svg, .cdr, .dxf, .pdf, .stl, .cnc, .imed, .png, .psd, .txt" @change="handleFileChange(index, $event)" />
                                    </div>
                                    <div class="col-md-1">
                                        <label>{{ $t('Remove') }}</label>
                                        <button class="btn default-base-color" @click="removeAttachment(index)">
                                            <app-icon class="size-19 primary-text-color" name="trash" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <a v-if="!isDisable" class="btn btn-primary cursor-pointer text-white" @click="addAttachment">Add Attachment</a>
                            <a v-if="!isDisable" class="btn btn-primary cursor-pointer text-white" @click="submitAttachments">Submit
                                Attachments</a>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <div v-if="lightboxSrc" @click="closeLightbox"
            style="position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.85); display:flex; align-items:center; justify-content:center; z-index:1080; cursor:zoom-out;">
            <img :src="lightboxSrc" style="max-width:92%; max-height:92%; box-shadow:0 0 24px rgba(0,0,0,0.6);" />
        </div>
    </fieldset>
</template>

<script>
import FormHelperMixins from "../../../../../common/Mixin/Global/FormHelperMixins";
import DefaultAction from "../../../../../core/components/datatable/DefaultAction";
import {
    SELECTABLE_CUSTOMER_GROUP,
    CUSTOMERS,
    ATTACHMENTS,
    BRANCH_OR_WAREHOUSE
} from "../../../../Config/ApiUrl-CPB";
import { LINKED_CUSTOMERS, WEIGHT_UNIT, GET_CUSTOMERS_NAME } from "../../../../Config/ApiUrl-CP";
import { axiosGet, axiosPost, axiosPatch, urlGenerator, axiosDelete } from "../../../../../common/Helper/AxiosHelper";
import { mapState, mapGetters } from "vuex";
import 'select2/dist/css/select2.css'; // Import Select2 CSS
import 'select2'; // Import Select2 JavaScript

export default {
    name: "PersonalInfo",
    mixins: [FormHelperMixins],
    props: {
        props: {
            customerId: {},
        },
    },
    components: {
        DefaultAction
    },
    data() {
        return {
            selectedUser: '',
            userList: [],
            userAssignedList: [],
            primary_complaint: [],
            weightUnit: '',
            attachments: [],
            referredBy: [],
            campList: [],
            attachmentDescriptions: {},
            loading: true,
            SELECTABLE_CUSTOMER_GROUP,
            CUSTOMERS,
            customerOptions: {
                url: urlGenerator("app/selectable-customer-groups"),
                query_name: "search_by_name",
                per_page: 10,
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: ({ id, name: value }) => ({ id, value }),
                prefetch: false,
            },
            actions: [
                {
                    title: this.$t('delete'),
                    icon: 'trash-2',
                    type: 'delete',
                    className: 'btn-dark text-primacy'
                }
            ],
            actionsAdd: [
                {
                    title: this.$t('add'),
                    icon: 'plus',
                    type: 'add',
                    className: 'text-primary',
                }
            ],
            typeList: [
                {
                    id: '',
                    name: this.$t('select_type'),
                },
                {
                    id: 'home',
                    name: this.$t('home'),
                },
                {
                    id: 'work',
                    name: this.$t('work'),
                },
                {
                    id: 'office',
                    name: this.$t('office'),
                }
            ],
            customerGroupInputKey: 0,
            originalGroupId: null,
            customerGroupOptions: {
                url: urlGenerator("app/selectable-customer-groups"),
                query_name: "search_by_name",
                per_page: 10,
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: ({ id, name: value }) => ({ id, value }),
                prefetch: false,
            },
            formData: {
                primary_complaint: '',
                customer_group_id: '',
                addresses: {},
                contacts: {},
                tin: '',
                tenant_id: '',
                gender: 'male',
                satisfaction_status: 'N/A',
                title: '',
                original_branch: '',
                referred_by: '',
                camp_name: '',
                record_created_by: window.user.full_name || '',
                registration_date: '', // Added registration_date property
                addToReport: false,
                first_assigned_to: null,
                currently_assigned_to: null,
                current_branch: '',
                diabetic: '',
            },
            branchList: [],
            primaryComplaintList: [],
            referredByList: [],
            linkedPatientsList: [],
            maxSelections: 3,
            Prefix: [
                { id: '', name: this.$t('Select Prefix') },
                { id: 'Mr.', name: this.$t('Mr.') },
                { id: 'Ms.', name: this.$t('Ms.') },
                { id: 'Mrs.', name: this.$t('Mrs.') },
                { id: 'Dr.', name: this.$t('Dr.') },
            ],
            CurrentUser: [
                { id: '', name: this.$t('Select User') },
                { id: window.user.full_name || '', name: this.$t(window.user.full_name || '') },
            ],
            GSTTypeList: [
                { id: '', name: this.$t('select_type') },
                { id: 'Unregistered', name: this.$t('Unregistered') },
                { id: 'Registered', name: this.$t('Registered') },
                { id: 'Composite', name: this.$t('Composite') },
            ],
            ExaminationVenue: [
                { id: '', name: this.$t('Select Examination Venue') },
                { id: 'Branch', name: this.$t('Branch') },
                { id: 'Home Visit', name: this.$t('Home Visit') },
                { id: 'Doctors Clinic', name: this.$t('Doctors Clinic') },
                { id: 'Online', name: this.$t('Online') },
                { id: 'Camp', name: this.$t('Camp') },
            ],
            ActivityLevel: [
                { id: '', name: this.$t('Select Activity Level') },
                { id: 'Home Based', name: this.$t('Home Based') },
                { id: 'Sedentary', name: this.$t('Sedentary') },
                { id: 'Morning Walks', name: this.$t('Morning Walks') },
                { id: 'On foot Occupation', name: this.$t('On foot Occupation') },
                { id: 'Recreational sport', name: this.$t('Recreational sport') },
                { id: 'Professional sport', name: this.$t('Professional sport') },
            ],
            LegacyPatient: [
                { id: '', name: this.$t('Select Legacy Patient') },
                { id: 'Yes', name: this.$t('Yes') },
                { id: 'No', name: this.$t('No') },
            ],
            PreferredLanguage: [
                { id: '', name: this.$t('Select Preferred Language') },
                { id: 'English', name: this.$t('English') },
                { id: 'Hindi', name: this.$t('Hindi') },
                { id: 'Marathi', name: this.$t('Marathi') },
            ],
            Source: [
                { id: '', name: this.$t('Select Source') },
                { id: 'Camp', name: this.$t('Camp') },
                { id: 'Google Business Page', name: this.$t('Google Business Page') },
                { id: 'Google Ad', name: this.$t('Google Ad') },
                { id: 'Website', name: this.$t('Website') },
                { id: 'Doctor', name: this.$t('Doctor') },
                { id: 'Walk-in', name: this.$t('Walk-in') },
                { id: 'YouTube', name: this.$t('YouTube') },
                { id: 'Facebook', name: this.$t('Facebook') },
                { id: 'Instagram', name: this.$t('Instagram') },
                { id: 'Referred by friend', name: this.$t('Referred by friend') },
                { id: 'Other', name: this.$t('Other') },
            ],
            customerOptions: {
                url: urlGenerator("app/selectable-customers"),
                queryName: 'search_by_name',
                per_page: 10,
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.full_name }),
            },
            campsOptions: {
                url: urlGenerator("app/selectable/camps"),
                queryName: 'search_by_name',
                per_page: 10,
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.camp_name }),
            },
            inputEmailRow: [{ email: '', email_type: '' }],
            inputAttachments: [],
            lightboxSrc: null,
            removableEmailRow: true,
            inputPhoneNumberRow: [{ phone_number: null, country_code: '+91' }],
            removablePhoneNumberRow: true,
            inputAddressRow: [],
            removableAddressRow: true,
            customerBranchId: null,
            isDisable: (this.$can('edit_patient_info') == true)?false:true,
            disableInput: null,
            showSecondaryEmail: false,
            showSecondaryPhone: false,
            phoneCountryCodes: [
                { code: '+91', name: 'India' },
                { code: '+1', name: 'USA/Canada' },
                { code: '+44', name: 'UK' },
                { code: '+61', name: 'Australia' },
                { code: '+971', name: 'UAE' },
                { code: '+65', name: 'Singapore' },
                { code: '+966', name: 'Saudi Arabia' },
                { code: '+60', name: 'Malaysia' },
                { code: '+49', name: 'Germany' },
                { code: '+33', name: 'France' },
                { code: '+81', name: 'Japan' },
                { code: '+86', name: 'China' },
                { code: '+880', name: 'Bangladesh' },
                { code: '+94', name: 'Sri Lanka' },
                { code: '+977', name: 'Nepal' },
                { code: '+92', name: 'Pakistan' },
                { code: '+27', name: 'South Africa' },
                { code: '+64', name: 'New Zealand' },
            ],
        }
    },
    created() {
        this.fetchCustomerBranchId();
        this.userListOptions();
        this.userListAssignedByOptions();
        this.branchListOptions();
        this.primaryComplaintOptions();
        if(window.user.roles[0].id != 1) {
            this.disableInput = true;
        }
        if(!this.isDisable) {
            this.isDisable = this.props[1];
        }
    },
    watch: {
        inputEmailRow() {
            this.removableEmailRow = this.inputEmailRow.length <= 1;
        },
        inputPhoneNumberRow() {
            this.removablePhoneNumberRow = this.inputPhoneNumberRow.length <= 1;
        },
        inputAddressRow() {
            this.removableAddressRow = this.inputAddressRow.length <= 1;
        },
        customer: {
            handler: function (customer) {
                this.formData = customer;
                $(".first_assigned_to,.original_branch,.currently_assigned_to").select2("destroy");
                $(".first_assigned_to,.original_branch,.currently_assigned_to").select2();
            }
        },

        'formData.date_of_birth'(newDateOfBirth) {
            if (newDateOfBirth) {
                const today = new Date();
                const birthDate = new Date(newDateOfBirth);
                const age = today.getFullYear() - birthDate.getFullYear();

                // Adjust age based on month and day of birth
                if (today.getMonth() < birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) {
                    this.formData.age = age - 1; // Update the age property directly
                } else {
                    this.formData.age = age; // Update the age property directly
                }
            }
        },
    },
    mounted() {
        // Initialize Select2
        let self = this;
        $('.first_assigned_to').select2({ placeholder: 'Select a user', }).on("change", function () {
            self.onSelectChangeFirstAssignedTo(this.value);
        });

        $('.original_branch').select2({ placeholder: 'Select a user', }).on("change", function () {
            self.formData.linked_patients = [];
            self.onSelectChangeOriginalBranch(this.value);
        });

        $('.currently_assigned_to').select2({ placeholder: 'Select a user', }).on("change", function () {
            self.onSelectChange(this.value);
        });

        // $('.primary_complaint').select2({ placeholder: 'Select a Primary Complaint', }).on("change", function () {
        //     self.onSelectChangePrimaryComplaint(this.value);
        // });
        
        $('.campname').select2({ placeholder: 'Select Camp name', }).on("change", function () {
            self.onSelectChangeCampName(this.value);
        });

        $('.referredby').select2({ placeholder: 'Select Referred by', }).on("change", function () {
            self.onSelectChangeReferredBy(this.value);
        });
    },
    methods: {
        showLoader(hide=false){
            $("div#app").addClass('apploader');
            if(hide) $("div#app").removeClass('apploader');
        },
        deleteAttachment(attachmentId) {
            axiosDelete(`app/delete-attachment/${attachmentId}`).then((response) => {
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
                this.toastAndReload(response.data.message);
            }).catch((error) => {
                // Handle any errors that occur during the request
                console.error(error);
            });
        },
        getAttachmentFileName(filename) {
            let nameArr = filename.split('/');
            return nameArr[nameArr.length-1];
        },
        isImageAttachment(filename) {
            if (!filename) return false;
            const ext = filename.split('.').pop().split('?')[0].toLowerCase();
            return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext);
        },
        openLightbox(src) {
            if (src) this.lightboxSrc = src;
        },
        closeLightbox() {
            this.lightboxSrc = null;
        },
        fetchCustomerBranchId() {
            axiosGet(`${CUSTOMERS}/${this.props[0]}`).then(response => {
                this.formData = response.data;
                this.originalGroupId = response.data.customer_group_id;
                this.customerGroupInputKey++;
                this.customerBranchId = response.data.original_branch;
                console.log('B', this.getBranchOrWarehouseId);

                // Call loadWeightUnit with customerBranchId as branchId
                this.loadWeightUnit(this.customerBranchId);
                // Call fetchlinkedCustomers with customerBranchId as branchId
                // this.fetchlinkedCustomers(this.customerBranchId);
                this.onSelectChangeOriginalBranch(this.customerBranchId);

                this.inputAttachments = this.formData.attachments;
                if (this.formData.contacts.email) {
                    this.inputEmailRow = this.formData.contacts.email.map((item) => {
                        return {
                            email: item.value,
                            email_type: item.type
                        }
                    });
                }

                if (this.formData.contacts.phone_number) {
                    this.inputPhoneNumberRow = this.formData.contacts.phone_number.map((item) => {
                        return {
                            phone_number: item.value,
                            country_code: item.type || '+91'
                        }
                    });
                }
                if (!this.inputEmailRow.length) this.inputEmailRow = [{ email: '', email_type: '' }];
                if (!this.inputPhoneNumberRow.length) this.inputPhoneNumberRow = [{ phone_number: null, country_code: '+91' }];
                this.showSecondaryEmail = this.inputEmailRow.length > 1;
                this.showSecondaryPhone = this.inputPhoneNumberRow.length > 1;
                if (Array.isArray(this.formData.linked_patients)) {
                    this.formData.linked_patients = this.formData.linked_patients.filter(Boolean).join(', ');
                }
            }).catch(error => {
                this.$toastr.e(error);
            }).finally(() => {
                this.loading = false;
                $(".first_assigned_to,.original_branch,.currently_assigned_to").select2("destroy");
                $(".first_assigned_to,.original_branch,.currently_assigned_to").select2();
            });
        },
        onSelectChangeFirstAssignedTo(val) {
            this.formData.first_assigned_to = val;
        },
        onSelectChangeOriginalBranch(val) {
            this.formData.original_branch = val;
            this.fetchLinkedCamps();
            this.fetchlinkedCustomers(val);
            this.getBranchData(val);
            // 'Referred by' source: B2B patients (group 3) list the B2B Partners;
            // everyone else lists the doctors. Decided by patient type (known now),
            // which is more reliable than waiting on the async branch lookup.
            if (this.formData.customer_group_id == 3) {
                this.fetchLinkedB2bPartners();
            } else {
                this.fetchLinkedDoctors();
            }
        },
        onSelectChangePrimaryComplaint(val) {
            this.formData.primary_complaint = val;
        },
        onSelectChangeReferredBy(val) {
            this.formData.referred_by = val;
        },
        onSelectChangeCampName(val) {
            this.formData.camp_name = val;
        },
        onSelectChange(val) {
            this.formData.currently_assigned_to = val;
        },
        userListOptions() {
            axiosGet(`app/selectable/users`).then(response => {
                for (const item of response.data.data) {
                    this.userList.push({
                        id: item.id,
                        value: item.full_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        userListAssignedByOptions() {
            axiosGet(`app/selectable/usersAll`).then(response => {
                for (const item of response.data.data) {
                    this.userAssignedList.push({
                        id: item.id,
                        value: item.full_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        referredByOptions() {
            axiosGet(`app/selectable/doctors`).then(response => {
                for (const item of response.data.data) {
                    // console.log("data", item);
                    this.referredByList.push({
                        id: item.id,
                        value: item.full_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        primaryComplaintOptions() {
            axiosGet(`app/selectable-pathologies`).then(response => {
                for (const item of response.data.data) {
                    // console.log("data", item);
                    this.primaryComplaintList.push({
                        id: item.id,
                        value: item.chief_complaint_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        branchListOptions() {
            axiosGet(`app/selectable/branches`).then(response => {
                for (const item of response.data.data) {
                    this.branchList.push({
                        id: item.id,
                        value: item.name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        fetchLinkedDoctors() {
            this.referredBy = [];
            axiosGet(`app/selectable/doctors?branch_id=${this.formData.original_branch}`).then(response => {
                for (const item of response.data.data) {
                    this.referredBy.push({
                        id: item.id,
                        value: item.full_name,
                    });
                }
                // The options and the saved value both arrive asynchronously, so
                // select2 (initialised once at mount) doesn't show the saved
                // 'Referred by' value on its own. After Vue renders the <option>s,
                // re-sync select2 to formData.referred_by so the saved value shows.
                this.$nextTick(() => {
                    $('.referredby').val(this.formData.referred_by).trigger('change.select2');
                });
            }).catch(error => {
                console.error(error);
            });
        },
        fetchLinkedCamps() {
            this.campList = [];
            axiosGet(`app/selectable/camps?branch_id=${this.formData.original_branch}`).then(response => {
                for (const item of response.data.data) {
                    this.campList.push({
                        id: item.id,
                        value: item.camp_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        getBranchData(branchId) {
            let branchDatanew = []; //`${BRANCH_OR_WAREHOUSE}/${row.id}`
            axiosGet(`${BRANCH_OR_WAREHOUSE}/${branchId}`).then(response => {
                branchDatanew = response.data;
                // ✅ If state ID exists, auto-select it in dropdown
                if (branchDatanew.stateid && branchDatanew.stateid !== "") {
                    this.inputAddressRow[this.index].area = branchDatanew.stateid;
                }
            }).catch(error => {
                console.error(error);
            });
        },
        fetchLinkedB2bPartners() {
            // Capture the saved value before clearing options — emptying the list can
            // wipe the v-model value when no matching <option> exists momentarily.
            const savedReferredBy = this.formData.referred_by;
            this.referredBy = [];
            axiosGet(`app/selectable/b2b-partners?branch_id=${this.formData.original_branch}`).then(response => {
                const items = (response.data && response.data.data) ? response.data.data : response.data;
                for (const item of (items || [])) {
                    this.referredBy.push({ id: item.id, value: item.name });
                }
                this.formData.referred_by = savedReferredBy;
                // select2 was initialised (empty) at mount and won't recognise options
                // added later by Vue, so re-initialise it after the options render and
                // set the saved value, then re-attach the change handler.
                this.$nextTick(() => {
                    const self = this;
                    const $sel = $('.referredby');
                    try { if ($sel.hasClass('select2-hidden-accessible')) $sel.select2('destroy'); } catch (e) { /* noop */ }
                    $sel.val(savedReferredBy);
                    $sel.select2({ placeholder: 'Select Referred by' });
                    $sel.off('change').on('change', function () { self.onSelectChangeReferredBy(this.value); });
                });
            }).catch(error => {
                console.error(error);
            });
        },
        onGenderChange(selectedGender) {
            // Access the selected gender
            this.formData.gender = selectedGender;
        },
        toggleSecondaryEmail() {
            if (!this.showSecondaryEmail) {
                if (this.inputEmailRow.length < 2) this.inputEmailRow.push({ email: '', email_type: '' });
                this.showSecondaryEmail = true;
            } else {
                if (this.inputEmailRow.length > 1) this.inputEmailRow.splice(1, 1);
                this.showSecondaryEmail = false;
            }
        },
        toggleSecondaryPhone() {
            if (!this.showSecondaryPhone) {
                if (this.inputPhoneNumberRow.length < 2) this.inputPhoneNumberRow.push({ phone_number: null, country_code: '+91' });
                this.showSecondaryPhone = true;
            } else {
                if (this.inputPhoneNumberRow.length > 1) this.inputPhoneNumberRow.splice(1, 1);
                this.showSecondaryPhone = false;
            }
        },
        getSourceColumnClass() {
            return this.formData.source === 'Camp' ? 'col-12 col-sm-6 col-md-4' : 'col-12 col-sm-6 col-md-4';
        },
        getCampColumnClass() {
            return this.formData.source === 'Camp' ? 'col-12 col-sm-6 col-md-4' : 'd-none'; // Hide if source is not 'Camp'
        },
        handleFileChange(index, event) {
            var allowedExtensions = ['jpg', 'jpeg', 'gif', 'cdr', 'dxf', 'pdf', '.svg', 'stl', 'cnc', 'imed', 'png', 'psd', 'txt'];
            const fileInput = event.target;
            const file = event.target.files[0];
            var fileName = file.name.toLowerCase();
            var fileExtension = fileName.split('.').pop();
            if (allowedExtensions.indexOf(fileExtension) === -1) {
                fileInput.value = '';
                alert('Invalid file type. Please select a valid file.');
                return false;
            } else {
                this.attachments[index].file = file;
            }
        },
        addAttachment() {
            this.attachments.push({ description: '', file: null });
        },
        removeAttachment(index) {
            this.attachments.splice(index, 1);
        },
        submitAttachments() {
            // Nothing to submit
            if (!this.attachments.length) {
                this.$toastr.e(this.$t('Please add an attachment first.'));
                return;
            }
            // Every attachment requires a file and a description
            for (const attachment of this.attachments) {
                if (!attachment.file) {
                    this.$toastr.e(this.$t('Please choose a file for each attachment.'));
                    return;
                }
                if (!attachment.description || !attachment.description.trim()) {
                    this.$toastr.e(this.$t('Description is required for each attachment.'));
                    return;
                }
            }

            const formData = new FormData();
            this.attachments.forEach((attachment) => {
                formData.append('attachment_descriptions[]', attachment.description);
                formData.append('attachments[]', attachment.file);
            });
            try {
                this.showLoader();
                axiosPost(`${ATTACHMENTS}/${this.props[0]}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }).then(response => {
                    // Handle response as needed
                    this.showLoader(true);
                    this.toastAndReload(response.data.message);
                    setTimeout(() => {
                        //window.location.reload()
                    }, 1500)
                }).catch(error => {
                    this.showLoader(true);
                    const msg = error.response && error.response.data && error.response.data.message
                        ? error.response.data.message : 'Error uploading attachment.';
                    this.$toastr.e(msg);
                    console.error(error);
                });

            } catch (error) {
                this.showLoader(true);
                console.error(error);
            }
        },
        loadWeightUnit(branchId) {
            axiosGet(`${WEIGHT_UNIT}/${branchId}`).then(response => {
                // console.log('response:', response.data.weight_unit);
                const weightUnit = response.data.weight_unit;

                // Set the weightUnit value in the data
                this.weightUnit = weightUnit;
            }).catch(error => {
                console.error(error);
            });
        },
        fetchlinkedCustomers(branchId) {
            this.linkedPatientsList = [];
            axiosGet(`${LINKED_CUSTOMERS}/${branchId}`).then(response => {
                for (const item of response.data.data) {
                    this.linkedPatientsList.push({
                        id: item.id.toString(),
                        value: item.full_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        handleLinkedPatientsChange() {
            console.log(this.formData.linked_patients.length);
            if (this.formData.linked_patients.length > this.maxSelections) {
                // If the user exceeds the maximum allowed selections, remove the latest added patients
                const removedPatients = this.formData.linked_patients.splice(this.maxSelections);
                // Show an error message to inform the user about the limit
                alert(`You can select up to ${this.maxSelections} linked patients.`);
            }
        },
        getCurrentDate() {
            const today = new Date();
            const year = today.getFullYear();
            let month = today.getMonth() + 1; // Note: month is zero-based
            let day = today.getDate();

            // Pad month and day with leading zeroes if necessary
            if (month < 10) {
                month = '0' + month;
            }
            if (day < 10) {
                day = '0' + day;
            }

            return `${year}-${month}-${day}`;
        },

        setInitialRegistrationDate() {
            this.formData.registration_date = this.getCurrentDate(); // Set initial value of registration_date
        },
        submitData() {
            this.fieldStatus.isSubmit = true;
            // this.loading = true;
            this.message = '';
            this.errors = {};

            this.formData.first_assigned_to = $($(".first_assigned_to")[0]).val();
            this.formData.currently_assigned_to = $($(".currently_assigned_to")[0]).val();
            this.formData.original_branch = $($(".original_branch")[0]).val();
            this.formData.primary_complaint = $($(".primary_complaint")[0]).val();
            this.formData.referred_by = $($(".referredby")[0]).val();
            this.formData.camp_name = $($(".campname")[0]).val();

            this.formData.email_details = this.inputEmailRow.map((item) => {
                return {
                    'email': item.email,
                    'email_type': item.email_type,
                }
            });

            this.formData.phone_number_details = this.inputPhoneNumberRow.map((item) => {
                return {
                    'phone_number': item.phone_number,
                    'phone_number_type': item.country_code || '+91',
                }
            });

            this.formData.attachments = this.attachments.map((attachment) => {
                return {
                    description: attachment.description,
                    file: attachment.file, // Include the file if needed
                };
            });

            // console.log(this.formData);
            this.showLoader();
            axiosPatch(`${CUSTOMERS}/${this.props[0]}`, this.formData)
                .then(response => {
                    this.showLoader(true);
                    this.toastAndReload(response.data.message);
                    setTimeout(() => {
                        window.location.reload()
                    }, 1500)
                })
                .catch(err => {
                    this.showLoader(true);
                    // Check if the error response contains a message
                    if (err.response && err.response.data && err.response.data.message) {
                        this.$toastr.e(err.response.data.message);
                        this.errors = err.response.data.errors || {};
                    } else {
                        // If there's no specific message in the error response, display a generic error message
                        this.$toastr.e('An error occurred while updating the customer.');
                    }
                });


        },

        afterSuccess(response) {
            this.$toastr.s('', response.data.message);
            setTimeout('', location.reload());
        },
        setDefaultGroupId() {
            this.axiosGet(SELECTABLE_CUSTOMER_GROUP).then((response) => {
                let id;
                response.data.filter((item) => {
                    if (item.is_default === 1)
                        id = item.id;
                })
                this.formData.customer_group_id = id;
            })
        },

        addEmailRow() {
            let checkEmptyRows = this.inputEmailRow.filter(
                line => line.number === null
            );
            if (checkEmptyRows.length >= 1 && this.inputEmailRow.length > 0) return;
            this.inputEmailRow.push({
                email: '',
                email_type: '',
            });
        },

        removeEmailRow(rowId) {
            if (!this.removableEmailRow) {
                this.inputEmailRow.splice(rowId, 1);
            }
        },

        addPhoneNumberRow() {
            let checkEmptyRows = this.inputPhoneNumberRow.filter(
                line => line.number === null
            );
            if (checkEmptyRows.length >= 1 && this.inputPhoneNumberRow.length > 0) return;
            this.inputPhoneNumberRow.push({
                phone_number: null,
                country_code: '+91',
            });
        },

        removePhoneNumberRow(rowId) {
            if (!this.removablePhoneNumberRow) {
                this.inputPhoneNumberRow.splice(rowId, 1);
            }
        },

        addAddress() {
            this.addAddressRow()
        },
        addAddressRow() {
            let checkEmptyRows = this.inputAddressRow.filter(
                line => line.number === null
            );
            if (checkEmptyRows.length >= 1 && this.inputAddressRow.length > 0) return;
            this.inputAddressRow.push({
                address_title: null,
            });
        },

        removeAddressRow(rowId) {
            if (!this.removableAddressRow) {
                this.inputAddressRow.splice(rowId, 1);
            }
        },
    },
    computed: {
        ...mapState({
            customer: state => state.customer.customer,
        }),

        doctorsOptions() {
            return {
                url: urlGenerator("app/selectable/doctors"),
                queryName: 'search_by_name',
                per_page: 50,
                params: { 'branch_id': this.getBranchOrWarehouseId, },
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.full_name }),
            }
        },

        userOptions() {
            return {
                url: urlGenerator("app/selectable/users"),
                queryName: 'search_by_name',
                per_page: 10,
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.full_name }),
            }
        },
        userCanUpdateCustomer() {
            return this.$can('update_customers');
        },
        canReassign() {
            const roles = (window.user && window.user.roles) || [];
            return roles.some(r => r.id == 1 || ['Super Admin', 'App Admin', 'Manager'].includes(r.name));
        },
        isWalkin() {
            return this.formData.customer_group_id == 2;
        },
        // A patient saved as Regular (1) cannot be switched to Walk-In later, so
        // drop the Walk-In option from the radio for such records.
        patientTypeOptions() {
            // B2B patients (group 3) are shown as B2B only.
            if (this.originalGroupId == 3 || this.formData.customer_group_id == 3) {
                return [{ id: 3, value: this.$t('B2B') }];
            }
            const opts = [
                { id: 1, value: this.$t('Regular Patient') },
                { id: 2, value: this.$t('Walk-In Customer') },
            ];
            return this.originalGroupId == 1 ? opts.filter(o => o.id !== 2) : opts;
        },
        regMaxDate() {
            return new Date();
        },
        regMinDate() {
            const d = new Date();
            d.setDate(d.getDate() - 2);
            return d;
        },

        ...mapGetters(['getBranchOrWarehouseName']),
        ...mapGetters(['getBranchOrWarehouseId']),
    },
}
</script>

<style>
.required>label:first-child:after {
    color: red;
    content: ' *';
    display: inline;
}

.section-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #2c3e50;
}

.walkin-mode .required:not(.always-required) > label:first-child:after {
    content: '';
}

/* Increase the width of the Select2 input */
.select2-container--default .select2-selection--single {
    width: 100%;
    border: 1px solid #ced4da;
}

/* Increase the height of the Select2 input */
.select2-container--default .select2-selection--single {
    height: 40px;
}

/* Increase padding and font size */
.select2-container--default .select2-selection--single .select2-selection__rendered {
    padding: 5px 10px;
    font-size: 16px;
}
</style>
<style lang="scss" scoped>
fieldset:disabled a.btn {
  pointer-events: all;
}
</style>